import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { useUser } from '/imports/hooks';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { RichSelect } from '/imports/ui/RichSelect';
import { Button } from '/imports/ui/Button';
import { listMUsers, MUser } from '/imports/api/musers';
import {
  blacklistMuser, unblacklistMuser, listBlacklistedMusers, Blacklist, blacklistKey,
} from '/imports/api/blacklists';

// Friendly blacklist editor: pick a muser by username (typeahead) to block,
// and remove blocked users from a list — no hand-editing records. Bots silently
// ignore trade invites from blocked usernames (see bot.ts acceptTrade).
export const BlacklistManager = () => {
  const user = useUser();
  const [rows, setRows] = useState<Blacklist[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Bumping this remounts the RichSelect so the picker resets after each add.
  const [pickerKey, setPickerKey] = useState(0);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    (async () => {
      try { setRows(await listBlacklistedMusers.call()); }
      catch (e: any) { toast.error(e?.message || `${e}`); }
      finally { setLoading(false); }
    })();
  }, []);

  if (user && !user.isSuperUser) return <NotPermittedPage />;

  const q = filter.trim().toLowerCase();
  const shown = q ? rows.filter((r) => r.username.toLowerCase().includes(q)) : rows;

  const add = async (username: string) => {
    if (rows.some((r) => r._id === blacklistKey(username))) return toast.info(`${username} is already blocked`);
    setBusy(true);
    try {
      setRows(await blacklistMuser.call({ username }));
      setPickerKey((k) => k + 1);
      toast.success(`Blocked ${username}`);
    }
    catch (e: any) { toast.error(e?.message || `${e}`); }
    finally { setBusy(false); }
  };

  const remove = async (username: string) => {
    setBusy(true);
    try {
      setRows(await unblacklistMuser.call({ username }));
      toast.success(`Unblocked ${username}`);
    }
    catch (e: any) { toast.error(e?.message || `${e}`); }
    finally { setBusy(false); }
  };

  return (
    <Page className="page model-list-page">
      <PageHeading left={<h1>Blacklist</h1>} />
      <PageBody>
        <p className="mb-3 text-sm text-grey-3">
          Bots silently ignore trade invitations from blocked users.
        </p>

        <div className="mb-6 max-w-md">
          <label className="mb-1 block text-sm font-semibold">Block a user by name</label>
          <RichSelect<MUser>
            key={pickerKey}
            placeholder="Search users by name…"
            listMethod={listMUsers}
            mapToValue={(m) => ({ value: String(m._id), label: m.name || String(m._id), data: m })}
            onChange={(option) => add(option.label)}
            disabled={busy}
          />
        </div>

        <div className="mb-2 flex max-w-md items-center justify-between gap-3">
          <h2 className="m-0">Blocked users ({rows.length})</h2>
          <input
            className="rounded border border-grey-6 px-2 py-1 text-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Find a blocked user…"
            autoComplete="off"
            disabled={loading || rows.length === 0}
          />
        </div>
        {loading ? (
          <p className="text-sm text-grey-3">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-grey-3">No users are blocked.</p>
        ) : shown.length === 0 ? (
          <p className="text-sm text-grey-3">No blocked users match “{filter.trim()}”.</p>
        ) : (
          <ul className="max-w-md [&>li]:flex [&>li]:items-center [&>li]:justify-between [&>li]:border-b [&>li]:border-grey-6 [&>li]:py-2">
            {shown.map((r) => (
              <li key={r._id}>
                <span className="text-sm">{r.username}</span>
                <Button className="btn small" onClick={() => remove(r.username)} disabled={busy}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </PageBody>
    </Page>
  );
};
